import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Alert as MuiAlert,
  Breadcrumbs as MuiBreadcrumbs,
  Paper,
  Snackbar,
  Table, TableBody, TableCell,
  TableContainer, TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import React, { useEffect, useState, useMemo } from 'react';
import FileSystemAccessService from '../../services/FileSystemAccessService';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import { formatDate, formatUserName } from '../../utils/formatUtils';
import AdminPageWrapper from './AdminPageWrapper';
import { mongoFilesBrowserHelpItems } from '../../utils/helpItems';

const MongoFilesBrowser = ({ darkMode, toggleDarkMode, themeVariant }) => {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [pathItems, setPathItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPath, setCurrentPath] = useState('/');
  const [pathHistory, setPathHistory] = useState(['/']);
  const [filter, setFilter] = useState('all'); // 'all', 'folders', 'files'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState('');
  const [newName, setNewName] = useState('');
  const [showRecursive, setShowRecursive] = useState(false);
  const [forceDelete, setForceDelete] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Sorting
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  // Get theme from context
  const theme = useTheme();

  const PROTECTED_PATHS = ['root'];

  const isProtectedPath = (item) => {
    if(!item.directory) return false
    return PROTECTED_PATHS.includes(item.name);
  };

  useEffect(() => {
    fetchItems(currentPath);
  }, [currentPath]);

  useEffect(() => {
    if (showRecursive) {
      // When showing recursively, we use all items
      const exactPath = currentPath.endsWith('/') ? currentPath : `${currentPath}/`;
      
      // For recursive view, include all items that are under the current path
      const recursiveItems = allItems.filter(item => 
        item.path.startsWith(exactPath) || item.path === currentPath
      );
      
      setItems(recursiveItems);
    } else {
      // When not showing recursively, use only direct children
      setItems(pathItems);
    }
  }, [showRecursive, allItems, pathItems, currentPath]);

  const fetchItems = async (path) => {
    try {
      setLoading(true);
      setError(null);
      
      // Using API v2 for MongoDB data
      const response = await FileSystemAccessService.listFilesAndFolders(path, 'v2');
      
      // Sort items: folders first, then files, both alphabetically
      const sortedItems = [...response.folders, ...response.files].sort((a, b) => {
        // First sort by type
        if (a.directory === true && b.directory !== true) return -1;
        if (a.directory !== true && b.directory === true) return 1;
        
        // Then sort alphabetically
        a.childrenCount = a.children ? a.children.length : 0;
        return a.name.localeCompare(b.name);
      });

      // Get items that belong to the current directory
      const exactPath = path.endsWith('/') ? path : `${path}/`;
      // Filter items that are direct children of the current path
      const pathItems = sortedItems.filter(item => {
        const itemParentPath = item.path.substring(0, item.path.lastIndexOf('/') + 1);
        return itemParentPath === exactPath || item.path === path;
      });
      
      setItems(pathItems);
      setPathItems(pathItems);
      setAllItems(sortedItems);
    } catch (err) {
      setError(`Error loading data: ${err.message}`);
      console.error("Error fetching files/folders:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchItems(currentPath);
  };

  const navigateToFolder = (path, name) => {
    // if name is not root then add it to the path
    if (name !== 'root') {
        path = `${path}${name}`;
    }
    // Update the current path
    setCurrentPath(path);
    // Add to history only if it's a forward navigation
    if (!pathHistory.includes(path)) {
      setPathHistory([...pathHistory, path]);
    }
  };

  const navigateBack = () => {
    // Find the previous path in history
    const currentIndex = pathHistory.indexOf(currentPath);
    if (currentIndex > 0) {
      const previousPath = pathHistory[currentIndex - 1];
      setCurrentPath(previousPath);
    }
  };

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const handleSearch = () => {
    // We could implement a more complex search here
    if (searchTerm.trim() === '') {
      fetchItems(currentPath);
    } else {
      const filteredItems = items.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setItems(filteredItems);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const openDialog = (action, item) => {
    if (action === 'delete' && isProtectedPath(item.name)) {
      setSnackbarMessage("Root folder cannot be deleted");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    
    setSelectedItem(item);
    setDialogAction(action);
    setNewName(item.name);
    setDialogOpen(true);
    setForceDelete(false); // Reset force delete option
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedItem(null);
  };


  const handleDelete = async () => {
    if (!selectedItem) return;
    
    try {
      if (selectedItem.directory === true) {
        await FileSystemAccessService.delete(
          selectedItem.path,
          null, null, forceDelete // Pass the force delete option
        );
      } else {
        await videoCRUDServiceInstance.delete(
          selectedItem.id);
      }
      
      closeDialog();
      handleRefresh();
      
      setSnackbarMessage(`Successfully deleted "${selectedItem.name}"`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setError(`Error deleting item: ${err.message}`);
      setSnackbarMessage(`Failed to delete: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Filter items based on type selection
  const filteredItems = items.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'folders') return item.directory === true;
    if (filter === 'files') return item.directory === false;
    return true;
  });

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedItems = filteredItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleBatchDelete = async () => {
    // Filter out protected items from selection
    const protectedItems = selectedItems.filter(item => isProtectedPath(item.path));
    const deletableItems = selectedItems.filter(item => !isProtectedPath(item.path));
    
    // If protected items were filtered out, show a warning
    if (protectedItems.length > 0) {
      setSnackbarMessage("Protected items were excluded from deletion");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
      
      // If no deletable items remain, return early
      if (deletableItems.length === 0) return;
    }
    
    // Continue with batch delete for non-protected items
    setLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    // First delete files, then folders (to avoid dependency issues)
    const files = deletableItems.filter(item => !item.directory);
    const folders = deletableItems.filter(item => item.directory);
    
    // Delete files first
    for (const file of files) {
      try {
        await videoCRUDServiceInstance.delete(file.id);
        successCount++;
      } catch (err) {
        console.error(`Error deleting file ${file.path}:`, err);
        errorCount++;
      }
    }
    
    // Then delete folders
    for (const folder of folders) {
      try {
        await FileSystemAccessService.delete(folder.path, null, null, forceDelete);
        successCount++;
      } catch (err) {
        console.error(`Error deleting folder ${folder.path}:`, err);
        errorCount++;
      }
    }
    
    setError(null);
    setLoading(false);
    setSelectedItems([]);
    
    if (errorCount === 0) {
      setSnackbarMessage(`Successfully deleted ${successCount} items`);
    } else {
      setSnackbarMessage(`Deleted ${successCount} items, ${errorCount} failed`);
    }
    setSnackbarSeverity(errorCount > 0 ? 'warning' : 'success');
    setSnackbarOpen(true);
    
    // Refresh the file list
    fetchItems(currentPath);
  };

  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      // Don't allow selecting protected items
      if (isProtectedPath(item.path)) {
        setSnackbarMessage("Root folder cannot be selected for batch operations");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        return;
      }
      setSelectedItems(prev => [...prev, item]);
    } else {
      setSelectedItems(prev => prev.filter(i => i.id !== item.id));
    }
  };

  const generateBreadcrumbs = (path) => {
    const parts = path.split('/').filter(part => part);
    let currentPath = '/';
    
    const crumbs = [{ name: 'Home', path: '/' }];
    
    for (const part of parts) {
      currentPath = `${currentPath}${part}/`;
      crumbs.push({ name: part, path: currentPath });
    }
    
    return crumbs;
  };

  // Get breadcrumbs based on current path
  const breadcrumbs = generateBreadcrumbs(currentPath);

  // Add this function to handle breadcrumb navigation
  const handleBreadcrumbClick = (path) => {
    setCurrentPath(path);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Add this helper function to sort items
  const sortedItems = React.useMemo(() => {
    if (!pathItems) return [];
    
    return [...pathItems].sort((a, b) => {
      // Always put directories first regardless of sort direction
      if (a.directory && !b.directory) return -1;
      if (!a.directory && b.directory) return 1;
      
      // Then sort by the selected column
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      // Handle special cases
      if (orderBy === 'updatedAt' || orderBy === 'createdAt') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }
      
      // Handle numeric and other types
      return order === 'asc' 
        ? (aValue < bValue ? -1 : 1)
        : (bValue < aValue ? -1 : 1);
    });
  }, [pathItems, order, orderBy]);

  // Add this function after the sorting-related code and near other handler functions
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      // If the checkbox is checked, select all non-protected items
      const nonProtectedItems = pathItems.filter(item => !isProtectedPath(item.path));
      setSelectedItems(nonProtectedItems);
      
      // Show a message if some items were excluded
      if (nonProtectedItems.length < pathItems.length) {
        setSnackbarMessage("Protected items were excluded from selection");
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
      }
      return;
    }
    // Otherwise clear selection
    setSelectedItems([]);
  };

  return (
    <AdminPageWrapper
      title="MongoDB Files Browser"
      helpItems={mongoFilesBrowserHelpItems}
      darkMode={darkMode}
      toggleDarkMode={toggleDarkMode}
      themeVariant={themeVariant}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }} >
        <Typography variant="h4">Files & Folders Browser</Typography>
        <Button 
          startIcon={<ArrowBackIcon />} 
          sx={{ ml: 'auto' }}
          onClick={() => window.history.back()}
          className="back-to-dashboard"
        >
          Back to Dashboard
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />} 
            onClick={navigateBack}
            disabled={currentPath === '/' || pathHistory.indexOf(currentPath) === 0}
          >
            Back
          </Button>
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }} className="breadcrumbs-navigation">
            <MuiBreadcrumbs separator="›" aria-label="breadcrumb" >
              {breadcrumbs.map((item, index) => (
                <Typography
                  key={index}
                  color={index === breadcrumbs.length - 1 ? 'text.primary' : 'primary'}
                  sx={{ 
                    cursor: index < breadcrumbs.length - 1 ? 'pointer' : 'default',
                    fontWeight: index === breadcrumbs.length - 1 ? 'bold' : 'normal'
                  }}
                  onClick={() => index < breadcrumbs.length - 1 && handleBreadcrumbClick(item.path)}
                >
                  {item.name}
                </Typography>
              ))}
            </MuiBreadcrumbs>
          </Box>

          {selectedItems.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={handleBatchDelete}
            >
              Delete {selectedItems.length} Selected
            </Button>
          )}
          
          <Button 
            variant="contained" 
            color="success" 
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Box>

        {error && <MuiAlert severity="error" sx={{ mb: 3 }}>{error}</MuiAlert>}
        
        <Box sx={{ mb: 2 }}>
          <Chip 
            icon={<FolderIcon />} 
            label={`${items.filter(i => i.directory === true).length} folders`} 
            color="primary"
            variant="outlined"
            sx={{ mr: 1 }}
          />
          <Chip 
            icon={<InsertDriveFileIcon />} 
            label={`${items.filter(i => i.directory !== true).length} files`} 
            color="secondary"
            variant="outlined"
            sx={{ mr: 1 }}
          />
          {showRecursive && (
            <Chip 
              label="Showing all subfolders" 
              color="info"
              variant="outlined"
            />
          )}
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table className="files-list" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedItems.length > 0 && selectedItems.length < pathItems.length}
                        checked={pathItems.length > 0 && selectedItems.length === pathItems.length}
                        onChange={handleSelectAllClick}
                      />
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleRequestSort('name')}
                      >
                        Name
                        {orderBy === 'name' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'directory'}
                        direction={orderBy === 'directory' ? order : 'asc'}
                        onClick={() => handleRequestSort('directory')}
                      >
                        Type
                        {orderBy === 'directory' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'path'}
                        direction={orderBy === 'path' ? order : 'asc'}
                        onClick={() => handleRequestSort('path')}
                      >
                        Path
                        {orderBy === 'path' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'childrenCount'}
                        direction={orderBy === 'childrenCount' ? order : 'asc'}
                        onClick={() => handleRequestSort('childrenCount')}
                      >
                        Items
                        {orderBy === 'childrenCount' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'createdAt'}
                        direction={orderBy === 'createdAt' ? order : 'asc'}
                        onClick={() => handleRequestSort('createdAt')}
                      >
                        Created
                        {orderBy === 'createdAt' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'createdBy'}
                        direction={orderBy === 'createdBy' ? order : 'asc'}
                        onClick={() => handleRequestSort('createdBy')}
                      >
                        Created By
                        {orderBy === 'createdBy' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'updatedAt'}
                        direction={orderBy === 'updatedAt' ? order : 'asc'}
                        onClick={() => handleRequestSort('updatedAt')}
                      >
                        Updated
                        {orderBy === 'updatedAt' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'updatedBy'}
                        direction={orderBy === 'updatedBy' ? order : 'asc'}
                        onClick={() => handleRequestSort('updatedBy')}
                      >
                        Updated By
                        {orderBy === 'updatedBy' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedItems.map((item) => {
                    const isProtected = isProtectedPath(item);
                    return (
                      <TableRow 
                        key={item.id}
                        hover
                        selected={selectedItems.some(selectedItem => selectedItem.id === item.id)}
                        sx={isProtected ? { 
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.03)'
                        } : {}}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox 
                            checked={selectedItems.some(i => i.id === (item.id || item._id))}
                            onChange={(e) => handleItemSelection(item, e.target.checked)}
                            disabled={isProtected}  // Disable checkbox for protected items
                          />
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            cursor: item.directory === true && !isProtected ? 'pointer' : 'default',
                            fontWeight: item.directory === true ? 'bold' : 'normal',
                            color: isProtected ? 'text.secondary' : (item.directory === true ? 'primary.main' : 'inherit')
                          }}
                          onClick={() => item.directory === true && navigateToFolder(item.path, item.name)}
                        >
                          {item.name}
                          {isProtected && (
                            <Chip 
                              label="Protected" 
                              size="small" 
                              color="secondary"
                              variant="outlined"
                              sx={{ ml: 1, fontSize: '0.7rem' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {item.directory === true ? (
                            <FolderIcon color="primary" />
                          ) : (
                            <InsertDriveFileIcon color="secondary" />
                          )}
                        </TableCell>
                        <TableCell>{item.path}</TableCell>
                        <TableCell>{item.childrenCount || 0}</TableCell>
                        <TableCell>
                          {formatDate(item.createdAt)}
                        </TableCell>
                        <TableCell>
                          {item.createdBy || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {formatDate(item.updatedAt)}
                        </TableCell>
                        <TableCell>
                          {formatUserName(item.updatedBy) || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            
                            <Tooltip title={isProtected ? "Protected folder cannot be deleted" : "Delete"}>
                              <span> {/* Wrap in span to allow tooltip on disabled button */}
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => openDialog('delete', item)}
                                  disabled={isProtected}  // Disable delete button for protected items
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </Paper>
      
      {/* Delete Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog}>
        <DialogTitle>
          {'Delete Item'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedItem?.name}"? This action cannot be undone.
          </Typography>
          
          {/* Only show force delete checkbox for folders */}
          {selectedItem?.directory === true && (
            <FormControlLabel
              control={
                <Checkbox 
                  checked={forceDelete}
                  onChange={(e) => setForceDelete(e.target.checked)}
                />
              }
              label="Force delete (delete even if folder is not empty)"
              sx={{ mt: 2, display: 'block' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button 
            onClick={handleDelete}
            color='error'
          >
            {'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <MuiAlert 
          elevation={6} 
          variant="filled" 
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </AdminPageWrapper>
  );
};

export default MongoFilesBrowser;